import { ContentDetailType, ContentType } from "@authory-packages/core";
import { SidebarLayoutShowMobile } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { truncate } from "../../utils/article-text-utils";
import { V3CaptionGrey35 } from "../ReusableStyledComponents/V3CaptionGrey35";
import { V3InputLabel } from "../SharedInputComponent";
import { V3Body, V3HeadlineH4 } from "../Typography";
import { VerticalSpacing } from "../VerticalSpacing";
import { MPMediaCard, MPMediaImage, PPPDFNote, PreviewNoContentSet } from "./ItemPreview.styles";
import { MAX_DESCRIPTION_SIZE } from "../../types/max_description_size";
import { ItemMetadata } from "../ItemMetadata";
import { CCCardDescription, CCCardDescriptionPlaceholder, CCCardTitle } from "../CollectionList/CollectionList.styles";
import { NO_DESCRIPTION_SET_COLLECTION_LABEL } from "../../utils/noDescriptionSetCollectionLabel";

export enum ItemPreviewVariant {
    Primary = "Primary",
    Secondary = "Secondary"
}

export interface ItemPreviewProps {
    date?: string;
    sourceName?: string;
    originalUrl?: string;
    contentType?: ContentType;
    imageSrc?: string;
    title?: string;
    description?: string;
    contentDetailType?: ContentDetailType;
    showMetaContent?: boolean;
    variant?: ItemPreviewVariant;
    hidePreviewTitle?: boolean;
    isCollection?: boolean;
}

export const ItemPreview = ({
    date,
    sourceName,
    originalUrl,
    contentType,
    contentDetailType,
    imageSrc,
    title,
    description,
    showMetaContent,
    variant = ItemPreviewVariant.Primary,
    hidePreviewTitle = false,
    isCollection = false
}: ItemPreviewProps) => {
    const isEmpty = !title?.length && !description?.length && !imageSrc?.length;
    const isPDF = contentType === ContentType.PdfFile;
    const isCustomVideo = contentDetailType === ContentDetailType.CustomVideo;
    const TitleToUse = !isCollection ? V3HeadlineH4 : CCCardTitle;
    const DescToUse = !isCollection ? V3Body : !description ? CCCardDescriptionPlaceholder : CCCardDescription;
    const finalDescription = isCollection && !description ? NO_DESCRIPTION_SET_COLLECTION_LABEL : description;

    return (
        <>
            {isEmpty ? (
                <PreviewNoContentSet>
                    <V3HeadlineH4>Enter title and description to see preview here.</V3HeadlineH4>
                </PreviewNoContentSet>
            ) : (
                <MPMediaCard $variant={variant} $isCollection={isCollection}>
                    <div>
                        {!hidePreviewTitle && (
                            <SidebarLayoutShowMobile>
                                <VerticalSpacing bottom={8}>
                                    <V3InputLabel>
                                        <strong>Preview</strong>
                                    </V3InputLabel>
                                </VerticalSpacing>
                            </SidebarLayoutShowMobile>
                        )}
                        {!!imageSrc && (
                            <VerticalSpacing bottom={24}>
                                <MPMediaImage image={imageSrc} $isCollection={isCollection} />
                            </VerticalSpacing>
                        )}
                        {isCustomVideo && !imageSrc && (
                            <VerticalSpacing bottom={24}>
                                <PPPDFNote>
                                    <V3CaptionGrey35>
                                        Note: If you don't upload an image, Authory will automatically generate a thumbnail of the video.
                                    </V3CaptionGrey35>
                                </PPPDFNote>
                            </VerticalSpacing>
                        )}
                        {isPDF && !imageSrc && (
                            <VerticalSpacing bottom={24}>
                                <PPPDFNote>
                                    <V3CaptionGrey35>
                                        Note: If you don't upload an image, Authory will automatically generate a thumbnail of the PDF.
                                    </V3CaptionGrey35>
                                </PPPDFNote>
                            </VerticalSpacing>
                        )}
                        <VerticalSpacing bottom={8}>
                            <TitleToUse $numLines={isCollection ? 1 : undefined} $allowUserThemable={false}>
                                {title}
                            </TitleToUse>
                        </VerticalSpacing>
                        <DescToUse $numLines={isCollection ? 2 : undefined} $allowUserThemable={false}>
                            {truncate(finalDescription || "", MAX_DESCRIPTION_SIZE)}
                        </DescToUse>
                        {showMetaContent && !!date && contentType && (
                            <VerticalSpacing top={12}>
                                <ItemMetadata
                                    item={{
                                        originalUrl: originalUrl ? originalUrl : null,
                                        date,
                                        sourceName,
                                        type: contentType
                                    }}
                                    showRedirect={false}
                                />
                            </VerticalSpacing>
                        )}
                    </div>
                </MPMediaCard>
            )}
        </>
    );
};
